<template>
  <b-modal
    id="reiseterminVerschiebenModal"
    :title="`Termin ${reiseterminkuerzelFormatted} verschieben`"
    centered
    @show="onShow"
    @hide="onHide"
  >
    <b-overlay :show="isLoading">
      <div class="mb-0">
        <FeldLabel class="mb-2" :icon="labelIcons.Calendar" text="Neues Abreisedatum" />
        <RestriktionenDatePicker
          v-model="date"
          :restriktionen-by-day="restriktionenByDay"
          land-als-hinweis
          :disabled-date="disabledBeforeToday"
          :belegte-tage="belegteTage"
          :default-value="startdatum"
          placeholder="Wähle ein Datum..."
        />
      </div>
    </b-overlay>
    <template #modal-footer="{ hide }">
      <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        @click="handleSubmit(hide)"
        :disabled="isSichernButtonDisabled"
        variant="primary"
      >
        Übernehmen
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { format, parse, addYears, endOfYear } from 'date-fns';
import { Calendar } from 'lucide-vue';
import apiService from '@/core/common/services/api.service';
import buildQuery from 'odata-query';
import RestriktionenDatePicker from '@/components/restriktionen/restriktionen-date-picker.vue';
import {
  toDayKey,
  groupRestriktionenByDay,
  mapKonfigurationToRestriktionen,
} from '@/components/restriktionen/restriktionen-calendar.helper.js';
import { getRestriktionskonfiguration } from '@/core/restriktionen/services/restriktionskonfiguration.service.js';
import { reiseToIsoCodes } from '@/core/common/iso3166Countries.js';
import FeldLabel from '@/components/common/feld-label.vue';

export default {
  props: {
    reiseterminkuerzel: { type: String, required: true },
    reiseterminId: { type: Number, required: true },
    reise: { type: Object, default: null },
  },
  components: { RestriktionenDatePicker, FeldLabel },
  data() {
    return {
      labelIcons: Object.freeze({ Calendar }),
      date: null,
      isLoading: false,
      restriktionen: [],
      belegteTage: {},
    };
  },
  watch: {
    date(newDate) {
      if (newDate) {
        this.isLoading = true;
        const abreisedatum = format(newDate, 'yyyy-MM-dd');

        apiService
          .get(
            `Reisetermin?$filter=state ne 'Verworfen' and abreisedatum eq ${abreisedatum} and reise/reisekuerzel eq '${this.reiseterminkuerzel.substring(
              0,
              6
            )}'&$expand=reise`
          )
          .then(res => {
            const { length } = res.data.result?.value;
            if (length != 0) {
              this.date = null;
              this.toast('Reisetermin existiert bereits.', { variant: 'danger' });
            }
          })
          .finally(() => (this.isLoading = false));
      }
    },
  },
  computed: {
    restriktionenByDay() {
      return groupRestriktionenByDay(this.restriktionen);
    },
    isSichernButtonDisabled() {
      return this.date == null || this.isLoading;
    },
    startdatum() {
      return parse(this.reiseterminkuerzel?.substring(7), 'yyyy-MM-dd', new Date());
    },
    reiseterminkuerzelFormatted() {
      const reisekuerzel = this.reiseterminkuerzel.substring(0, 6);
      const startdatum = format(
        parse(this.reiseterminkuerzel.substring(7), 'yyyy-MM-dd', new Date()),
        'dd.MM.yyyy'
      );

      return reisekuerzel + ' ' + startdatum;
    },
  },
  methods: {
    handleSubmit(hide) {
      apiService
        .post('Reisetermin/terminVerschieben', {
          ursprungsterminId: this.reiseterminId,
          neuerTerminDate: format(this.date, 'yyyy-MM-dd'),
        })
        .then(() => {
          this.toast('Bitte denke daran, terminspezifische Daten in die Termine neu einzupflegen.', {
            variant: 'warning',
            noCloseButton: false,
            noAutoHide: true,
            title: 'Hinweis!',
          });

          this.$emit('terminVerschoben');
        });
      hide();
    },
    onShow() {
      this.ladeRestriktionen();
      this.ladeBelegteTage();
    },
    onHide() {
      this.date = null;
      this.belegteTage = {};
      this.resetRestriktionen();
    },
    // Bricht einen laufenden Restriktionen-Request ab und leert die geladenen Restriktionen
    resetRestriktionen() {
      if (this.$_restriktionenAbortController) {
        this.$_restriktionenAbortController.abort();
        this.$_restriktionenAbortController = null;
      }
      this.restriktionen = [];
    },
    // Hier werden die Restriktionen der Reise und ihrer Länder für den Kalender geladen
    async ladeRestriktionen() {
      this.resetRestriktionen();
      // Die ersten sechs Zeichen des Terminkürzels bilden das Reisekürzel
      const reisekuerzel = this.reiseterminkuerzel?.substring(0, 6);
      if (!reisekuerzel) {
        return;
      }

      const controller = new AbortController();
      this.$_restriktionenAbortController = controller;

      try {
        const result = await getRestriktionskonfiguration({
          von: format(new Date(), 'yyyy-MM-dd'),
          bis: format(endOfYear(addYears(new Date(), 3)), 'yyyy-MM-dd'),
          laender: reiseToIsoCodes(this.reise),
          reisen: [reisekuerzel],
          signal: controller.signal,
        });
        // Das Ergebnis eines zwischenzeitlich abgebrochenen Requests wird verworfen
        if (controller.signal.aborted) {
          return;
        }
        this.restriktionen = mapKonfigurationToRestriktionen(result);
      } finally {
        if (this.$_restriktionenAbortController === controller) {
          this.$_restriktionenAbortController = null;
        }
      }
    },
    // Hier werden Abreisedatum und Status der vorhandenen Termine der Reise geladen
    async ladeBelegteTage() {
      const reisekuerzel = this.reiseterminkuerzel?.substring(0, 6);
      if (!reisekuerzel) {
        return;
      }
      const heute = new Date();
      heute.setHours(0, 0, 0, 0);
      const query = buildQuery({
        filter: {
          'reise/reisekuerzel': reisekuerzel,
          abreisedatum: { ge: heute },
          isDeleted: false,
        },
        select: ['abreisedatum', 'state'],
      });
      const res = await apiService.get('Reisetermin' + query);
      this.belegteTage = Object.fromEntries(
        (res.data.result?.value ?? []).map(rt => [toDayKey(rt.abreisedatum), rt.state])
      );
    },
    // Tage vor heute sind im Kalender nicht wählbar
    disabledBeforeToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return date < today;
    },
  },
};
</script>

<style scoped></style>
